import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Layers, Printer, FileDown } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

const API = process.env.REACT_APP_BACKEND_URL;
const download = async (path, filename) => {
  const res = await fetch(`${API}${path}`, { credentials: "include",
    headers: { "Authorization": `Bearer ${localStorage.getItem("erp_token") || ""}` } });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob(); const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
};

export default function BatchWiseStock() {
  const [data, setData] = useState({ rows: [], count: 0, total_qty: 0, total_value: 0 });
  const pg = usePagination(data?.rows || [], data);
  const [categories, setCategories] = useState([]);
  const [f, setF] = useState({ q: "", category: "all", exp_from: "", exp_to: "", include_zero: false });

  useEffect(() => {
    api.get("/categories").then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (f.q.trim()) p.set("q", f.q.trim());
    if (f.category !== "all") p.set("category", f.category);
    if (f.exp_from) p.set("exp_from", f.exp_from);
    if (f.exp_to) p.set("exp_to", f.exp_to);
    if (f.include_zero) p.set("include_zero", "true");
    return p.toString();
  }, [f]);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get(`/stocks/batches?${qs}`).then(r => setData(r.data)).catch(e => toast.error(formatErr(e)));
    }, 200);
    return () => clearTimeout(t);
  }, [qs]);

  return (
    <div className="p-8 print:p-4">
      <div className="flex items-end justify-between mb-6 print:mb-3">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2 print:hidden"><Layers className="w-3 h-3"/> Stocks · Stock Data</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Batch Wise Stock</h1>
          <div className="text-xs text-muted-foreground mt-1">Stock with batch details, filtered by category and expiry range</div>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button data-testid="batch-stock-export" variant="outline" onClick={() => download(`/api/stocks/batches/export.csv?${qs}`, "batch_stock_report.csv").catch(e => toast.error(formatErr(e)))} className="rounded-sm text-xs uppercase tracking-wider"><FileDown className="w-3 h-3 mr-1"/> Export CSV</Button>
          <Button data-testid="batch-stock-print" variant="outline" onClick={() => window.print()} className="rounded-sm text-xs uppercase tracking-wider"><Printer className="w-3 h-3 mr-1"/> Print</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-3 border border-border bg-card p-3 rounded-sm print:hidden">
        <div className="col-span-2">
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Search</Label>
          <Input data-testid="batch-stock-search" value={f.q} onChange={(e) => setF(x => ({ ...x, q: e.target.value }))} placeholder="Item / Part / Batch #…" className="mt-1 rounded-sm bg-secondary border-border h-9"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Category</Label>
          <Select value={f.category} onValueChange={(v) => setF(x => ({ ...x, category: v }))}>
            <SelectTrigger data-testid="batch-stock-filter-category" className="mt-1 rounded-sm bg-secondary border-border h-9"><SelectValue/></SelectTrigger>
            <SelectContent><SelectItem value="all">All categories</SelectItem>{categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Expiry From</Label>
          <Input data-testid="batch-stock-exp-from" type="date" value={f.exp_from} onChange={(e) => setF(x => ({ ...x, exp_from: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border h-9"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Expiry To</Label>
          <Input data-testid="batch-stock-exp-to" type="date" value={f.exp_to} onChange={(e) => setF(x => ({ ...x, exp_to: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border h-9"/>
        </div>
        <label className="flex items-end gap-2 pb-1 text-xs cursor-pointer"><input data-testid="batch-stock-zero" type="checkbox" checked={f.include_zero} onChange={(e) => setF(x => ({ ...x, include_zero: e.target.checked }))} className="accent-primary"/> Include zero-qty</label>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3 text-xs print:hidden">
        <div className="border border-border bg-card rounded-sm p-3"><div className="text-muted-foreground uppercase tracking-wider">Batches</div><div className="text-2xl font-display font-black text-primary" data-testid="batch-stock-count">{data.count}</div></div>
        <div className="border border-border bg-card rounded-sm p-3"><div className="text-muted-foreground uppercase tracking-wider">Total Qty</div><div className="text-2xl font-display font-black" data-testid="batch-stock-qty">{data.total_qty}</div></div>
        <div className="border border-border bg-card rounded-sm p-3"><div className="text-muted-foreground uppercase tracking-wider">Stock Value (Cost)</div><div className="text-2xl font-display font-black" data-testid="batch-stock-value">₹{data.total_value.toLocaleString("en-IN")}</div></div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="batch-stock-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Item Name</th>
              <th className="text-left p-3">Part No</th>
              <th className="text-left p-3">Batch No</th>
              <th className="text-left p-3">Expiry Date</th>
              <th className="text-left p-3">Unit</th>
              <th className="text-right p-3">Quantity</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground" data-testid="batch-stock-empty">No batches match the selected filters</td></tr>}
            {pg.pageItems.map(r => (
              <tr key={r.batch_id} data-testid={`batch-row-${r.batch_id}`} className={`border-t border-border ${r.expired ? "bg-destructive/10" : r.expiring_soon ? "bg-yellow-500/10" : "hover:bg-secondary/40"}`}>
                <td className="p-3">{r.item_name}</td>
                <td className="p-3 font-mono text-xs text-primary uppercase">{r.part_number || "-"}</td>
                <td className="p-3 font-mono text-xs">{r.batch_number}</td>
                <td className="p-3 text-xs">{r.expiry_date || "—"}</td>
                <td className="p-3 text-xs text-muted-foreground">{r.unit}</td>
                <td className="p-3 text-right font-mono font-semibold">{r.qty_remaining}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
